<template>
  <main>
    <section class="flex flex-col gap-5">
      <form @submit.prevent="submitQuiz">
        <div class="bg-white rounded-md shadow-md p-5">
          <div class="flex justify-between items-center">
            <p class="font-bold">{{ $t("quiz.teacher.info") }}</p>
            <Button
              id="btn-save-info"
              :label="$t('labels.save', { pt: 'informações', en: 'info' })"
              class="btn-outline p-button-sm"
              :loading="loading"
              type="submit"
              v-if="role === 'TEACHER'"
            />
          </div>
          <div class="grid grid-cols-12 gap-5 mt-2">
            <div class="flex flex-col col-span-12 md:col-span-2">
              <p class="mb-2">{{ $t("quiz.teacher.duration") }}</p>
              <InputMask
                id="duration-input"
                mask="99:99:99"
                v-model="form.duration"
                placeholder="00:00:00"
                :class="v$.form.duration.$error ? 'p-invalid' : ''"
              />
              <Errors :errors="v$.form.duration.$errors" />
            </div>
            <div class="flex flex-col col-span-12 md:col-span-4">
              <p class="mb-2">{{ $t("quiz.teacher.total") }}</p>
              <InputNumber
                inputId="total-questions-input"
                v-model="form.numberQuestions"
                :class="v$.form.numberQuestions.$error ? 'p-invalid' : ''"
                :min="1"
                :max="100"
              />
              <Errors :errors="v$.form.numberQuestions.$errors" />
            </div>
          </div>
        </div>
      </form>
    </section>
  </main>
</template>

<script lang="ts">
import { required } from "@/locale/locale.config";
import { CreateQuiz } from "@/models/quiz.model";
import { QuizRest } from "@/services/rest/quiz.rest";
import { GenericsUtils } from "@/utils/generics.util";
import useVuelidate from "@vuelidate/core";
import { delay } from "rxjs";
import { defineComponent } from "vue";
import { ObjectiveService } from "../../objective.service";
import { helpers } from "@vuelidate/validators";

export default defineComponent({
  name: "formQuiz",
  emits: ["onAllow", "onCreated"],
  props: {
    data: [Object],
    objective: [Object],
    course: [Object],
    role: [String],
  },
  computed: {
    rest(): QuizRest {
      return new QuizRest();
    },
    service(): ObjectiveService {
      return new ObjectiveService();
    },
    generics(): GenericsUtils {
      return new GenericsUtils();
    },
    chapterId(): string {
      return this.$route.params.chapterId.toString();
    },
  },
  data() {
    return {
      form: new CreateQuiz(),
      loading: false as boolean,
      objectiveLocal: {} as any,
      statusQuiz: false as boolean,
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      form: {
        duration: {
          required,
          time: helpers.withMessage(
            this.$t("validations.time"),
            (value: string) => {
              const timeRegex = /^(0[0-9]:[0-5][0-9]:[0-5][0-9]|10:00:00)$/;
              return timeRegex.test(value);
            }
          ),
        },
        numberQuestions: { required },
      },
    };
  },
  mounted() {
    this.getQuiz();
  },
  methods: {
    getQuiz(): void {
      this.form = this.data ? (this.data as CreateQuiz) : this.form;
    },
    create(objectiveId: any, data: CreateQuiz): void {
      this.rest
        .createQuiz(objectiveId, data)
        .pipe(delay(500))
        .subscribe({
          next: (response) => {
            this.loading = false;
            this.form.id = response.id;
            this.$emit("onAllow", 3);
            this.$emit("onCreated", response.id);
            this.statusQuiz = true;
            this.enableQuiz(true);
          },
          error: () => {
            this.loading = false;
          },
        });
    },
    update(objectiveId: any, data: CreateQuiz): void {
      this.rest
        .updateQuiz(objectiveId, data)
        .pipe(delay(500))
        .subscribe({
          next: (response) => {
            this.loading = false;
            this.$emit("onCreated", response.id);
          },
          error: () => {
            this.loading = false;
          },
        });
    },
    submitQuiz(): void {
      const objectiveId = this.objective?.id;
      this.v$.form.$touch();
      if (!this.v$.form.$error) {
        this.loading = true;
        const data = new CreateQuiz();
        data.id = this.form.id ?? undefined;
        data.courseId = String(this.$route.params.courseId);
        data.duration = this.generics.stringToSeconds(
          String(this.form.duration)
        );
        data.learningObjectiveId = objectiveId;
        data.numberQuestions = this.form.numberQuestions;

        if (this.form?.id) {
          this.update(objectiveId, data);
        } else {
          this.create(objectiveId, data);
        }
      }
    },
    enableQuiz(value: boolean): void {
      const data: any = this.objective;
      data.existsQuiz = value;
      this.service.updateObjective(
        this.chapterId,
        this.objective?.id || "",
        data
      );
    },
  },
});
</script>
